import { useEffect, useState } from "react";
import { DollarSign, Calendar, Users, Building2, MapPin, Stethoscope, Award } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { PageHead, KpiCard, TableSkeleton, Money } from "./ui";

const PIE_COLORS = ["#F4A261", "#264653", "#7209B7", "#2A9D8F", "#E76F51", "#94A3B8"];
const actionColor = (a, sub) => {
  const t = (sub || a || "").toLowerCase();
  if (t.includes("book")) return "text-emerald-600";
  if (t.includes("regist") || t.includes("login")) return "text-blue-600";
  if (t.includes("consent")) return "text-amber-600";
  if (t.includes("eras") || t.includes("delet") || t.includes("anonym")) return "text-rose-600";
  return "text-gray-600";
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  const [pie, setPie] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get("/admin/dashboard/stats").then((r) => setStats(r.data));
    api.get("/admin/revenue/trend?range=12m").then((r) =>
      setTrend(r.data.labels.map((l, i) => ({ month: l, revenue: r.data.values[i] }))));
    api.get("/admin/dashboard/bookings-by-status").then((r) =>
      setPie(Object.entries(r.data.counts).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }))));
    api.get("/admin/dashboard/activity?limit=20").then((r) => setActivity(r.data.activity));
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <PageHead title="Dashboard" subtitle="Platform overview & key metrics" />
      {!stats ? <TableSkeleton rows={4} /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KpiCard testid="kpi-revenue" label="Total Revenue" value={<Money value={stats.total_revenue} />}
            sub={`${stats.revenue_change_pct >= 0 ? "▲" : "▼"} ${Math.abs(stats.revenue_change_pct)}% vs last month`}
            icon={DollarSign} tint="#2A9D8F" />
          <KpiCard testid="kpi-bookings" label="Active Bookings" value={stats.active_bookings}
            sub={`${stats.total_bookings} total`} icon={Calendar} tint="#264653" />
          <KpiCard testid="kpi-users" label="Registered Users" value={stats.registered_users}
            sub={`+${stats.new_users_month} this month`} icon={Users} tint="#7209B7" />
          <KpiCard testid="kpi-hospitals" label="Partner Hospitals" value={stats.partner_hospitals}
            icon={Building2} tint="#0D5C75" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-black/5" data-testid="revenue-trend-chart">
          <h2 className="font-heading font-bold text-[#111827] mb-4">Revenue Trend (12 months)</h2>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
              <XAxis dataKey="month" fontSize={11} tickLine={false} />
              <YAxis fontSize={11} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
              <Line type="monotone" dataKey="revenue" stroke="#2A9D8F" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-black/5" data-testid="bookings-status-chart">
          <h2 className="font-heading font-bold text-[#111827] mb-4">Bookings by Status</h2>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                {pie.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Legend /><Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <KpiCard label="Top City" value={stats.top_city} icon={MapPin} tint="#E76F51" />
          <KpiCard label="Top Specialty" value={stats.top_specialty} icon={Stethoscope} tint="#F4A261" />
          <KpiCard label="Top Provider" value={stats.top_provider} icon={Award} tint="#0D5C75" />
        </div>
      )}

      <div className="rounded-2xl bg-white p-5 shadow-sm border border-black/5 mt-6" data-testid="activity-feed">
        <h2 className="font-heading font-bold text-[#111827] mb-4">Recent Activity</h2>
        <div className="max-h-80 overflow-y-auto divide-y divide-black/5">
          {activity.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
          {activity.map((a, i) => (
            <div key={i} className="py-2.5 flex items-center gap-3 text-sm">
              <span className="text-xs text-muted-foreground w-36 shrink-0">{(a.timestamp || "").slice(0, 16).replace("T", " ")}</span>
              <span className="text-[#111827] truncate flex-1">{a.actor}</span>
              <span className={`font-semibold ${actionColor(a.action, a.sub_action)}`}>{a.sub_action || a.action}</span>
              <span className="text-muted-foreground truncate max-w-[160px]">{a.resource_type}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
